import { useEffect, useRef, useState } from "react";

// Keep off-screen carousel artwork out of the initial network burst. Native
// lazy loading uses a deliberately generous preload distance and can fetch an
// entire horizontal rail even though most of it is outside the viewport.
const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

const DeferredImage = ({
  src,
  srcSet,
  sizes,
  fallbackSrc,
  alt = "",
  rootMargin = "100px",
  onError,
  ...props
}) => {
  const imageRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!src || shouldLoad) return undefined;

    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin }
    );

    if (imageRef.current) observer.observe(imageRef.current);
    return () => observer.disconnect();
  }, [rootMargin, shouldLoad, src]);

  const handleError = (event) => {
    if (fallbackSrc && event.currentTarget.src !== fallbackSrc) {
      event.currentTarget.srcset = "";
      event.currentTarget.src = fallbackSrc;
    }
    onError?.(event);
  };

  return (
    <img
      ref={imageRef}
      src={shouldLoad ? src : TRANSPARENT_PIXEL}
      data-src={shouldLoad ? undefined : src}
      srcSet={shouldLoad ? srcSet : undefined}
      data-srcset={shouldLoad ? undefined : srcSet}
      sizes={shouldLoad ? sizes : undefined}
      alt={alt}
      decoding="async"
      onError={handleError}
      {...props}
    />
  );
};

export default DeferredImage;
